import { camelCase } from 'lodash-es';
import { parseJSONString } from '@intelligence/utils/common';
import { isEmpty } from '@intelligence/utils/commonUtils';
import { NewMarkDisplayMode, NewMarkType } from '@intelligence/enums/common';
import { DBIMenuType } from '@intelligence/enums/menu';
import { STATIC_MENU_CFG_MAPPING } from '@intelligence/_config/menu';
import ICON_MENU_UGC from '@intelligence/assets/svg/menu/icon-menu-ugc.svg';

import type { IDBIMenuDTO, IDBIMenuParams, IDBIMenuTag, IDBIMenuVO } from '@intelligence/types/menu';
import type { INewMarkCfg } from '@intelligence/types/newMark';

const generateIPLibraryPathTitle = (title?: string): string => {
  if (!title) return '';
  return camelCase(title);
};

const analysisMenuPath = (menuType: DBIMenuType, menuParams?: IDBIMenuParams | null): string => {
  if (!menuType) return '';
  const { path } = menuParams || {};
  let pathTmp = '';

  switch (menuType) {
    case DBIMenuType.Directory: {
      pathTmp = '';
      break;
    }
    case DBIMenuType.RoutePage: {
      pathTmp = path || '';
      const searchParams = new URLSearchParams();

      // 如果存在 filter_tag_name 参数，追加到 URL 查询参数中
      const filterTagName = menuParams?.filter_tag_name?.trim() || '';
      if (filterTagName) {
        searchParams.set('filter_tag_name', encodeURIComponent(filterTagName));
      }

      // tab_cond_keys 从后端透传，供前端 Tab 切换时复用
      if (!isEmpty(menuParams?.tab_cond_keys)) {
        searchParams.set(
          'tab_cond_keys',
          encodeURIComponent(JSON.stringify(menuParams?.tab_cond_keys)),
        );
      }

      const queryString = searchParams.toString();
      pathTmp = !pathTmp || !queryString ? pathTmp : `${pathTmp}?${queryString}`;
      break;
    }
    case DBIMenuType.ThirdPage: {
      pathTmp = path || '';
      break;
    }
    case DBIMenuType.DatalabReportPage: {
      const {
        id,
        title,
        page_title: pageTitle,
        game_code: gameCode,
        add_title: addTitle,
        page_log_config_key: pageLogConfigKey,
      } = menuParams || {};
      const searchParams = new URLSearchParams({
        id: id || '',
        title: encodeURIComponent(title || ''),
        pageTitle: pageTitle || '',
        gameCode: gameCode || '',
        pageLogConfigKey: pageLogConfigKey || '',
      });
      if (addTitle === '0') {
        pathTmp = `${path}?${searchParams.toString()}`;
      } else {
        const pathTitle = generateIPLibraryPathTitle(title);
        pathTmp = `${path}/${pathTitle}?${searchParams.toString()}`;
      }
      break;
    }
    case DBIMenuType.TableauPage: {
      const { id } = menuParams || {};
      const searchParams = new URLSearchParams({ id: id || '' });
      pathTmp = `${path}?${searchParams.toString()}`;
      break;
    }
    case DBIMenuType.AuthPath: {
      pathTmp = `authPath/report/${encodeURIComponent(menuParams?.title || '')}`;
      break;
    }
    case DBIMenuType.COSPage: {
      const { path: routePath, lowcode_template_key, page_log_key, ...restParams } = menuParams || {};
      const searchParams = new URLSearchParams();

      // Add lowcode_template_key if present
      if (lowcode_template_key) {
        searchParams.set('lowcode_template_key', lowcode_template_key);
      }

      // Add page_log_key if present
      if (page_log_key) {
        searchParams.set('page_log_key', page_log_key);
      }

      // Add any other parameters from restParams (excluding internal fields)
      Object.entries(restParams).forEach(([key, value]) => {
        if (value && key !== 'title' && key !== 'game_code' && typeof value === 'string') {
          searchParams.set(key, value);
        }
      });

      const queryString = searchParams.toString();
      pathTmp = queryString ? `${routePath}?${queryString}` : routePath || '';
      break;
    }
  }

  return pathTmp || '';
};

const NEW_MARK_DISPLAY_MODE_MAPPING: Record<number, NewMarkDisplayMode> = {
  0: NewMarkDisplayMode.RecordControlled,
  1: NewMarkDisplayMode.Fixed,
};

const NEW_MARK_TYPE_MAPPING: Record<string, NewMarkType> = {
  NEW: NewMarkType.New,
  BETA: NewMarkType.Beta,
};

const getMenuNewMarkExpireDate = (tag?: IDBIMenuTag): string => {
  const expireDate = tag?.label_expire_date || '';
  return expireDate?.trim() || '';
};

export const generateMenusNewMarkCfg = (menus?: IDBIMenuVO[] | null): Record<string, INewMarkCfg> => {
  if (!Array.isArray(menus) || !menus.length) return {};
  const map: Record<string, INewMarkCfg> = {};

  for (const menu of menus) {
    const { id, tag, subMenus } = menu || {};
    const { label_type: labelType, label } = tag || {};
    if (!isEmpty(labelType) && !isEmpty(label)) {
      map[id] = {
        newMarkDisplayMode: NEW_MARK_DISPLAY_MODE_MAPPING[labelType!] ?? NewMarkDisplayMode.RecordControlled,
        newMarkType: NEW_MARK_TYPE_MAPPING[label!] ?? NewMarkType.New,
        newMarkExpireDate: getMenuNewMarkExpireDate(tag),
      };
    }

    if (Array.isArray(subMenus) && !!subMenus.length) {
      Object.assign(map, generateMenusNewMarkCfg(subMenus));
    }
  }

  return map;
};

export const formatMenus = (menus?: IDBIMenuDTO[] | null): IDBIMenuVO[] => {
  if (!Array.isArray(menus) || menus.length === 0) return [];
  return menus.map((menu) => {
    const {
      id,
      parent_id: parentMenuId,
      type,
      spread_type: defaultOpened,
      title_en: title,
      title_cn: titleCN,
      sub_menus: subMenus,
      params,
      tag,
      ...restMenu
    } = menu || {};

    const { className } = STATIC_MENU_CFG_MAPPING[id] || {};
    const paramsObj = parseJSONString<IDBIMenuParams>({}, params);
    const tagObj = parseJSONString<IDBIMenuTag>({}, tag);
    const isLocalIcon = id === '3900';
    const localIcon = id === '3900' ? ICON_MENU_UGC : '';

    return {
      ...restMenu,
      // REMARK: 虽然情报菜单是有后端控制，但是由于前端home仓LeftMenu组件影响，env必须配置pre、pro，否则对应环境就不会展示了
      env: ['pre', 'pro'],
      id: id || '',
      parentMenuId: parentMenuId || '',
      type,
      defaultOpened: defaultOpened || false,
      title: title || '',
      titleCN: titleCN || '',
      path: analysisMenuPath(type, { ...paramsObj, title: title || '' }) || '',
      className: className || '',
      params: paramsObj || {},
      tag: tagObj || {},
      subMenus: formatMenus(subMenus),
      isLocalIcon,
      localIcon,
    };
  });
};
